// src/pages/CRM/CrmCalendar.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Calendar, Views } from 'react-big-calendar'
import { Spin, Card, Typography } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../stores/root-store-context'
import ERPLayout from '../../components/layout/ERPLayout'
import { useNavigate } from 'react-router-dom'
import { localizer } from '../Extended Calendar/utils'

dayjs.extend(utc)
const { Title } = Typography

function CrmCalendar() {
  const { t, i18n } = useTranslation()
  const { crmStore, applicationStore } = useStore()
  const navigate = useNavigate()

  const [range, setRange] = useState<{ start: Date; end: Date }>({
    start: dayjs().startOf('month').toDate(),
    end: dayjs().endOf('month').toDate(),
  })
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      await crmStore.getCrmEvents()
    } finally {
      setLoading(false)
    }
  }, [crmStore])

  useEffect(() => {
    void load()
  }, [load])

  const events = useMemo(() => {
    return crmStore.CrmEvents.filter(e => {
      const s = dayjs(e.dateStart).toDate()
      const en = dayjs(e.dateEnd).toDate()
      return en >= range.start && s <= range.end
    }).map(e => ({
      id: `crm_${e.id}`,
      title: e.name,
      start: dayjs(e.dateStart).toDate(),
      end: dayjs(e.dateEnd).toDate(),
    }))
  }, [crmStore.CrmEvents, range])

  const onRangeChange = (r: any) => {
    const [start, end] = Array.isArray(r) ? [r[0], r[r.length - 1]] : [r.start, r.end]
    setRange({ start, end })
  }

  const handleSelectEvent = (ev: { id: string }) => {
    const raw = ev.id as string
    const id = raw.startsWith('crm_') ? raw.slice(4) : raw
    navigate(`/${applicationStore.company?.Tenant}/crm/event/${id}`)
  }

  const eventStyleGetter = () => ({ style: { backgroundColor: '#d48806', color: '#fff' } })

  const breadcrumbItems = [
    { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
    { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
    { label: 'Calendar', path: `/${applicationStore.company?.Tenant}/crm/calendar` },
  ]

  return (
    <ERPLayout breadcrumbTitle="CRM" breadcrumbItems={breadcrumbItems}>
      <div className="container" style={{ margin: '20px auto 50px' }}>
        <Card bordered={false}>
          <Title level={3} style={{ marginBottom: 24 }}>
            <CalendarOutlined /> {t('CRM Calendar')}
          </Title>

          <Spin spinning={loading}>
            <div style={{ height: 700, background: '#fff', padding: 16, borderRadius: 8 }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                views={[Views.MONTH, Views.WEEK, Views.DAY] as any}
                culture={i18n.language}
                style={{ height: '100%' }}
                onRangeChange={onRangeChange}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
              />
            </div>
          </Spin>
        </Card>
      </div>
    </ERPLayout>
  )
}

export default observer(CrmCalendar)
